import { useState, useEffect } from 'react';

export function useMediaQuery(query: any) {
  // Always initialise to false so the server-rendered HTML and the initial
  // client render agree on the same value, avoiding React hydration mismatches.
  // The useEffect below immediately corrects the value after hydration using
  // the actual media query result, so any layout shift is deferred to after
  // the browser has painted the hydrated page rather than causing a mismatch.
  const [matches, setMatches] = useState<boolean>(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (!media) return;

    // Sync in case query changed between render and effect
    setMatches(media.matches);
    const listener = (e: MediaQueryListEvent) => setMatches(e.matches);

    // Some older browsers/WebViews (pre-Chromium Edge, old iOS WebViews, some
    // in-app browsers) implement MediaQueryList but only expose the deprecated
    // addListener/removeListener API — addEventListener is undefined on those.
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', listener);
      return () => media.removeEventListener('change', listener);
    } else if (typeof (media as any).addListener === 'function') {
      // Deprecated fallback — safe to use here since we only reach this branch
      // on environments that don't support addEventListener
      (media as any).addListener(listener);
      return () => (media as any).removeListener(listener);
    }
  }, [query]);

  return matches;
}
